import type { Tenant } from '@t4s/api-client/model'
import { useQuery } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { fetchTenant, tenantQueryKey } from './gateway.ts'
import { Kicker, ModalDialog, UnknownEnvironmentNotice } from './modal-dialog.tsx'

/**
 * Retiring a tenant — docs/ux/PLATFORM-ADMIN.md §15.3, the strongest confirmation in this
 * application.
 *
 * It is a modal `<dialog>` that lists what retirement does **with this tenant's real
 * numbers**, **read fresh each time it opens**, and requires the owner to type the tenant's
 * slug before the danger button is usable (§19).
 *
 * **Fresh means a request, not the page's cache.** The page's tenant read can be thirty
 * seconds old by the time somebody has made up their mind, and "6 sign-ins are active right
 * now" is the one sentence in this dialog that must be true at the moment it is read. So the
 * dialog observes the same tenant query with `staleTime: 0` and `refetchOnMount: 'always'`:
 * opening it asks again (an answer already in flight is shared, not duplicated), the two
 * count lines read "Reading the current numbers…" until it answers, and **only a successful
 * answer to that request is shown as a number** — a failed refresh leaves older data in the
 * cache, and showing it would be exactly the stale figure this exists to prevent.
 *
 * **Escape does not close it.** A stray Escape must not be the difference between retiring
 * and not; its own Cancel is a real button and is the only way out (§5.3). The shell
 * (`ModalDialog`) refuses it and returns focus to the control that opened this.
 *
 * **It counts sign-ins, and says "sign-ins".** The tenant read offers `liveSessions` — sessions —
 * and no number of accounts that have one, so one person signed in on two devices is two. The
 * sentence used to say "people" (P06 per-task QA, D3); it now matches the Overview's "Signed in
 * now · live sessions" and the notice after retiring ("N sign-ins were ended").
 *
 * The effects it names are §15.1's, and it tells the truth about all of them — including the
 * one that is easy to leave out because it is inconvenient: the slug stays reserved forever.
 * (It no longer names a hostname teardown: there is none, §15.1, amended 2026-08-23.)
 *
 * **A count that fails to load says so and still allows the action.** A confirmation that
 * cannot read a number must not become a lock.
 */

export type RetireDialogProps = {
  /** The tenant as the page holds it: name and slug only. The numbers are read again here. */
  readonly tenant: Tenant
  readonly pending: boolean
  /** A failed confirm's sentence, shown inside the dialog so the typed slug is kept. */
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onConfirm: () => void
}

export function RetireDialog({ tenant, pending, error, onCancel, onConfirm }: RetireDialogProps) {
  const [typed, setTyped] = useState('')
  const slugId = useId()
  const headingId = useId()

  const fresh = useQuery({
    queryKey: tenantQueryKey(tenant.tenantId),
    queryFn: () => fetchTenant(tenant.tenantId),
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const reading = fresh.isFetching
  const counts = fresh.status === 'success' && !reading ? fresh.data : undefined

  return (
    <ModalDialog labelledBy={headingId}>
      <Kicker lead="Irreversible" />
      <h2 id={headingId}>Retire {tenant.name}?</h2>

      <UnknownEnvironmentNotice />

      <ul>
        <li>
          <strong>
            {counts !== undefined
              ? `${counts.liveSessions} ${counts.liveSessions === 1 ? 'sign-in is' : 'sign-ins are'} active right now, and will be ended.`
              : reading
                ? 'Reading the current numbers…'
                : 'Somebody may be signed in right now. This console could not read how many; they will be signed out either way.'}
          </strong>
        </li>
        <li>
          {counts !== undefined
            ? `${counts.users.active} ${counts.users.active === 1 ? 'account' : 'accounts'} will no longer be able to sign in.`
            : reading
              ? 'Reading the current numbers…'
              : 'This console could not read how many accounts will lose access.'}
        </li>
        <li>
          This tenant’s builds stop working: its app can no longer reach its login screen, its
          configuration or its branding.
        </li>
        <li>
          The slug <code>{tenant.slug}</code> stays reserved <strong>forever</strong>. It cannot be
          given to another tenant, now or later.
        </li>
        <li>Nothing is deleted. Its reports, users, assignments and media stay where they are.</li>
      </ul>

      <p className="confirm-warning">
        There is no un-retire in this console. Reversing this is not a button here.
      </p>

      <div className="typed">
        <label htmlFor={slugId}>
          Type <code>{tenant.slug}</code> to confirm
        </label>
        <input
          id={slugId}
          className="mono"
          value={typed}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => {
            setTyped(event.target.value)
          }}
        />
        {typed === tenant.slug ? (
          <span className="typed-match" role="status">
            Matches — the button is enabled.
          </span>
        ) : null}
      </div>

      {error === undefined ? null : (
        <p className="notice notice-failure" role="alert">
          {error}
        </p>
      )}

      <div className="confirm-controls">
        <button type="button" className="button" onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button
          type="button"
          className="button button-danger"
          disabled={typed !== tenant.slug || pending}
          onClick={onConfirm}
        >
          {pending ? 'Retiring…' : 'Retire this tenant'}
        </button>
      </div>
    </ModalDialog>
  )
}
